import React from 'react';

// Card for `kind: "sensor.verdict"` — the verdict-only output of
// SensorAnalyzer.assess. Pairs visually with `sensor.aggregation`
// (raw data) when both are emitted in the same Workflow chain.
//
// Expected fields:
//   verdict             — short label (e.g. "anomaly_high_humidity", "normal")
//   verdict_confidence  — "high" | "med" | "low"
//   verdict_reason      — one-sentence explanation citing the numbers
//   suggested_next      — list of follow-up tool names (advisory)
//   minutes             — lookback window
//   key                 — reading key filter (or null)
//   n_readings_considered — count over the window
export default function SensorVerdictCard({ structured }) {
  const {
    verdict, verdict_confidence: conf, verdict_reason: reason,
    suggested_next: nexts, minutes, key, n_readings_considered: n,
  } = structured;

  const tone = _toneFor(verdict, conf);
  const filter = key ? ` · ${key}` : '';

  return (
    <div style={{
      marginBottom: '10px', padding: '12px',
      background: tone.bg, borderRadius: '8px',
      border: `1px solid ${tone.border}`,
      borderLeft: `4px solid ${tone.accent}`,
    }}>
      <div style={{
        display: 'flex', alignItems: 'baseline', gap: '8px',
        marginBottom: '6px',
      }}>
        <span style={{
          fontSize: '0.7rem', fontWeight: 700,
          color: tone.accent, textTransform: 'uppercase',
          letterSpacing: '0.05em',
        }}>
          Verdict
        </span>
        <span style={{
          fontSize: '0.9rem', fontWeight: 600, color: '#0f172a',
          fontFamily: '"IBM Plex Mono", monospace',
        }}>
          {verdict || '(none)'}
        </span>
        {conf && (
          <span style={{
            fontSize: '0.7rem', color: '#64748b',
            marginLeft: 'auto',
          }}>
            confidence: <b>{conf}</b>
          </span>
        )}
      </div>

      {reason && (
        <div style={{
          fontSize: '0.85rem', color: '#334155',
          lineHeight: 1.45, marginBottom: nexts && nexts.length ? '8px' : 0,
        }}>
          {reason}
        </div>
      )}

      {nexts && nexts.length > 0 && (
        <div style={{ fontSize: '0.7rem', color: '#64748b' }}>
          suggested next: {nexts.map((t, i) => (
            <span key={i} style={{
              fontFamily: '"IBM Plex Mono", monospace', color: '#475569',
              marginRight: '6px',
            }}>{t}</span>
          ))}
        </div>
      )}

      <div style={{
        fontSize: '0.65rem', color: '#94a3b8', marginTop: '6px',
      }}>
        last {minutes ?? 60} min{filter}{n != null ? ` · ${n} readings` : ''}
      </div>
    </div>
  );
}

function _toneFor(verdict, conf) {
  const v = (verdict || '').toLowerCase();
  if (v === 'normal') {
    return { bg: '#f0fdf4', border: '#bbf7d0', accent: '#15803d' };
  }
  if (v.startsWith('anomaly')) {
    if (conf === 'high') {
      return { bg: '#fef2f2', border: '#fecaca', accent: '#b91c1c' };
    }
    return { bg: '#fffbeb', border: '#fde68a', accent: '#b45309' };
  }
  return { bg: '#f8fafc', border: '#e2e8f0', accent: '#475569' };
}
